import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { withHousehold } from 'Context/HouseholdContext';
import { validateType } from 'Helpers/Validation';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { useTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { addQuery } from 'Helpers/Query';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import { uniqBy } from 'lodash';
import Typography from '@material-ui/core/Typography';
import ChipGroup from 'Components/UI/ChipGroup';
import Detail from './EventDetail';
import TimelineEvents from './Events';
import useBackgroundColor from '../../../../../../Hooks/useBackgroundColor';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.facet.lightGray,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.facet.lightGray2}`,
  },
  selectGroup: {
    '& :not(:first-child)': {
      display: 'flex',
    },
  },
  select: {
    minWidth: '170px',
    padding: '0',
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    '& div.MuiSelect-select': {
      padding: theme.spacing(1),
    },
    '& label': {
      transform: 'translate(14px, 10px) scale(1)',
    },
  },
  datePicker: {
    width: '180px',
  },
  clearFiltersButton: {
    backgroundColor: theme.palette.common.white,
  },
  eventsContainer: {
    display: 'flex',
    height: 'calc(100vh - 394px)',
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(10),
    marginRight: theme.spacing(10),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.facet.lightGray,
  },
  eventColumn: {
    flex: 1,
  },
  events: {
    backgroundColor: theme.palette.facet.lightGray,
    overflowY: 'scroll',
    boxShadow: `-10px 0 20px ${theme.palette.facet.lightGray2}`,
    marginRight: theme.spacing(1),
  },
  eventsDetail: {
    display: 'flex',
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.facet.lightGray,
    overflowY: 'scroll',
  },
  eventsDetailSelected: {
    backgroundColor: theme.palette.common.white,
  },
  eventDetail: {
    display: 'flex',
    boxShadow: `-10px 0 20px ${theme.palette.facet.lightGray2}`,
    overflowY: 'scroll',
  },
  noEventMessage: {
    color: theme.palette.facet.primaryBlue,
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    backgroundColor: theme.palette.facet.lightGray,
  },
  chipGroupHolder: {
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  chipGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: theme.spacing(1),
  },
  noEventMessageContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  timelineEventsEvent: {}, // Adding for overrides from parents
});

const excludedOptionTypes = ['All', 'Date Picker'];

const transformTimelineOptions = options => options.map(({
  Name, Value, Selected, MultipleSelect,
}) => ({
  label: Name, value: Value, selected: Selected, multiselect: MultipleSelect,
}));

const filterFalse = (category, filter) => {
  return filter[category].reduce((selected, option) => selected && option.Selected === false, true);
};

// timeline events do not have unique ids, so we need a way to tell them apart somehow
const getEventId = event => `${event.id}${event.eventTime}${event.category}${event.eventKey}${event.status_id}`;

const initialRequest = {
  All: [
    {
      Independent: false,
      MultipleSelect: false,
      Name: 'All',
      Selected: false,
      Value: 'all',
    },
  ],
  'Frequently Used': [
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Meetings',
      Selected: true,
      Value: 'Calendar Value',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Agendas',
      Selected: true,
      Value: 'agendas',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Personal Emails',
      Selected: true,
      Value: 'email',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Notes',
      Selected: true,
      Value: 'note',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Escalations',
      Selected: true,
      Value: 'escalation',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Tasks',
      Selected: true,
      Value: 'tasks',
    },
  ],
  'Date Range': [
    {
      Independent: true,
      MultipleSelect: false,
      Name: 'Yesterday',
      Selected: false,
      Value: '-1',
    },
    {
      Independent: true,
      MultipleSelect: false,
      Name: 'Last 15 Days',
      Selected: false,
      Value: '-15',
    },
    {
      Independent: true,
      MultipleSelect: false,
      Name: 'Last 30 Days',
      Selected: true,
      Value: '-30',
    },
    {
      Independent: true,
      MultipleSelect: false,
      Name: 'Last 60 Days',
      Selected: false,
      Value: '-60',
    },
    {
      Independent: true,
      MultipleSelect: false,
      Name: 'Last 90 Days',
      Selected: false,
      Value: '-90',
    },
  ],
  'Other Items': [
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Calls',
      Selected: false,
      Value: 'call',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Other Emails',
      Selected: false,
      Value: 'otherEmail',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Internal Request',
      Selected: false,
      Value: 'kissflow',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'User Actions',
      Selected: false,
      Value: 'action',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Uploaded Files',
      Selected: false,
      Value: 'secureFile',
    },
    {
      Independent: false,
      MultipleSelect: true,
      Name: 'Events',
      Selected: false,
      Value: 'userEvent',
    },
  ],

};

const Timeline = ({ classes, className }) => {
  const { getTimelineEventsByHouseholdID } = useSwaggerlessContext();
  const [filterOptions, setFilterOptions] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(initialRequest);
  const [filterOrder, setFilterOrder] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [pagination, setPagination] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [limit, setLimit] = useState(-1);
  const [offset, setOffset] = useState(-1);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eventAttributes, setEventAttributes] = useState({});
  const [endOfTimelineEvents, setEndOfTimelineEvents] = useState(false);
  const {
    rootNodeProps,
    logEvent,
    logEventSuccess,
    logEventFailureWithError,
  } = useEventTrackingLogEvent({
    nodeName: 'Timeline',
    isPageView: true,
  });

  const theme = useTheme();
  useBackgroundColor(theme.palette.facet.lightGray);

  const { householdResource } = useSimpleHousehold();

  const fetchTimelineData = async (fetchLimit = -1, fetchOffset = -1, query = '', householdResourceId = householdResource.id()) => {
    const filter = JSON.stringify(selectedOptions);
    try {
      const { data: { attributes, meta } } = await getTimelineEventsByHouseholdID(householdResourceId, {
        filter,
        query,
        s: fetchOffset,
        l: fetchLimit,
      });
      const { att, met } = validateType(attributes, meta);
      const { Filter: options, FilterOrder: order, Pagination: metaPagination } = met;
      const { timeBandEvent: events } = att;
      setEndOfTimelineEvents(events.length === 0);

      return {
        options, order, metaPagination, events, attributes: att,
      };
    } catch (e) {
      displayErrorMessage(e, 'Could not fetch events');
      return null;
    }
  };

  const timelineReloadListener = useCallback(() => {
    fetchTimelineData().then(({ events }) => {
      setTimelineEvents(events);

      if (selectedEvent) {
        setSelectedEvent(current => events.find(event => getEventId(event) === getEventId(current)));
      }
    });
  }, [householdResource, selectedEvent]);


  useEffect(() => {
    if (householdResource) {
      setLoading(true);
      fetchTimelineData().then((result) => {
        if (!result) return;
        const {
          options, order, metaPagination, events, attributes,
        } = result;
        setFilterOrder(order);
        setFilterOptions(options);
        setPagination(metaPagination);
        setTimelineEvents(events);
        setLimit(parseInt(metaPagination.NextLimit, 10));
        setOffset(parseInt(metaPagination.NextOffset, 10));
        setEventAttributes(attributes);

        if (!initialized) {
          setInitialized(true);
          setSelectedOptions(options);
        }
      })
        .catch((err) => {
          displayErrorMessage(err, 'Could not fetch timeline events');
        })
        .finally(() => setLoading(false));
    }
  }, [householdResource, selectedOptions]);

  useEffect(() => {
    window.addEventListener('timelineReload', timelineReloadListener);

    return () => {
      window.removeEventListener('timelineReload', timelineReloadListener);
    };
  }, [timelineReloadListener]);

  const handleFilterSelection = (key, value) => {
    // for later use, we want to get a list of all the filters that are not independent, i.e. filters that effect
    // whehter or not 'All' is selected
    const categories = Object.entries(selectedOptions)
      .filter(([, values]) => !values[0].Independent)
      .map(([category]) => category);

    if (Array.isArray(value)) {
      setSelectedOptions((prev) => {
        const next = { ...prev };
        next[key].forEach((option) => {
          // eslint-disable-next-line no-param-reassign
          option.Selected = value.includes(option.Value);
        });

        // All is true if all non-independent filters are false
        next.All[0].Selected = categories.reduce((res, category) => res && filterFalse(category, next), true);
        return next;
      });
    } else {
      const index = selectedOptions[key].findIndex(({ Value }) => Value === value);

      setSelectedOptions((prev) => {
        const next = { ...prev };

        next[key].forEach((option, optionIndex) => {
          if (!value) {
            // eslint-disable-next-line no-param-reassign
            option.Selected = false;
          } else {
            // eslint-disable-next-line no-param-reassign
            option.Selected = optionIndex === index && !option.Selected;
          }
        });

        // All is true if all non-independent filters are false, but we only update if the key we're updating is not an
        // independent filter
        if (!next[key][0].Independent) {
          next.All[0].Selected = categories.reduce((res, category) => res && filterFalse(category, next), true);
        }

        return next;
      });
    }

    logEvent('Clicked Timeline Filter Chip', {
      filterName: key,
      filterValue: value,
    });
    setEndOfTimelineEvents(false);
    setSelectedEvent(null);
  };

  const clearFilters = () => {
    setSelectedOptions((prev) => {
      const next = { ...prev };

      Object.keys(next).forEach((key) => {
        next[key].forEach((option) => {
          // eslint-disable-next-line no-param-reassign
          option.Selected = false;
        });
      });

      next.All[0].Selected = true;

      return next;
    });

    setEndOfTimelineEvents(false);
    setSelectedEvent(null);
    logEvent('Clicked Clear Filters');
  };

  const orderedFilterOptions = (filterOptions && filterOrder
    ?.reduce((options, key) => {
      if (excludedOptionTypes.includes(key)) {
        return options;
      }

      return options.concat([[key, transformTimelineOptions(filterOptions[key])]]);
    }, [])) || [];

  const openTodoModal = (todoId) => {
    addQuery({ todoId });
    openModal(modalConsts.simpleActionItemModal);
  };

  const handleAddEvent = () => {
    logEvent('Clicked Add Event');
    openModal(modalConsts.timelineNewEventDialog);
  };

  const handleLoadMore = async () => {
    logEvent('Clicked Load More on Timeline');
    setLoading(true);
    try {
      const { events, metaPagination } = await fetchTimelineData(limit, offset);
      setTimelineEvents(currentEvents => uniqBy(currentEvents.concat(events), getEventId));
      setPagination(metaPagination);
      setLimit(parseInt(metaPagination.NextLimit, 10));
      setOffset(parseInt(metaPagination.NextOffset, 10));
      logEventSuccess('Loaded more events on Timeline');
    } catch (err) {
      displayErrorMessage(err, 'Could not fetch timeline events');
      logEventFailureWithError('Loaded more events on Timeline', err);
    } finally {
      setLoading(false);
    }
  };

  const [dateOptions, ...rest] = orderedFilterOptions;

  const optionToChipGroup = ([key, values]) => {
    const val = transformTimelineOptions(selectedOptions?.[key] || [])
      .filter(({ selected }) => selected)
      .map(({ value }) => value);
    return (
      <ChipGroup
        className={classes.chipGroup}
        key={key}
        multiselect={values[0].multiselect}
        options={values}
        onSelect={s => handleFilterSelection(key, s)}
        value={val}
        title={key}
      />
    );
  };

  return (
    <div
      className={classNames(classes.root, className)}
      {...rootNodeProps}
    >
      <div className={classes.header}>
        <div className={classes.selectGroup}>
          <div>
            {dateOptions && optionToChipGroup(dateOptions)}
          </div>
          <div className={classes.chipGroupHolder}>
            {rest?.map(optionToChipGroup)}
          </div>
          <Button
            className={classes.clearFiltersButton}
            variant="outlined"
            label="Clear filters"
            onClick={clearFilters}
            test="clear-filters"
          >
            Clear filters
          </Button>
        </div>
      </div>
      <div className={classes.eventsContainer}>
        <div className={classNames(classes.events, classes.eventColumn)}>
          <TimelineEvents
            classes={{
              event: classes.timelineEventsEvent,
            }}
            timelineEvents={timelineEvents}
            selectedEvent={selectedEvent}
            pagination={pagination}
            loading={loading}
            onEventClick={(e) => {
              logEvent('Clicked Timeline Event');
              setSelectedEvent(e);
              if (e.category === 'task') {
                openTodoModal(e.id);
              }
            }}
            onAddEvent={handleAddEvent}
            onLoadMore={handleLoadMore}
            endOfTimelineEvents={endOfTimelineEvents}
          />
        </div>
        <div
          className={classNames(classes.eventColumn, classes.eventDetail, {
            [classes.eventsDetailSelected]: !!selectedEvent,
          })}
        >
          {selectedEvent
            ? <Detail
              className={classes.eventDetail}
              event={selectedEvent}
              householdResource={householdResource}
              attributes={eventAttributes}
            />
            : (
              <div className={classes.noEventMessageContainer}>
                <Typography
                  variant="h1"
                  className={classes.noEventMessage}
                >
                  {'< Select Item'}
                </Typography>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
};

Timeline.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

Timeline.defaultProps = {
  className: '',
};

export default withHousehold(withStyles(styles)(Timeline));
